import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, CalendarDays, Clock, DollarSign, User, FileText, Home } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StripeSecurityNote from "@/components/StripeSecurityNote";

const STATUS_COLORS = {
  confirmed: "bg-green-100 text-green-800", pending_approval: "bg-amber-100 text-amber-800",
  pending_deposit: "bg-orange-100 text-orange-800", pending: "bg-amber-100 text-amber-800",
  rescheduled: "bg-blue-100 text-blue-800", completed: "bg-[#E6DEC3] text-[#635B52]",
  cancelled: "bg-red-100 text-red-700", converted: "bg-green-100 text-green-800",
  quote_sent: "bg-amber-100 text-amber-800", submitted: "bg-amber-100 text-amber-800",
  photo_review: "bg-blue-100 text-blue-800", more_photos_needed: "bg-orange-100 text-orange-800",
  accepted: "bg-green-100 text-green-800", declined: "bg-red-100 text-red-700",
  paid_in_full: "bg-green-100 text-green-800", deposit_paid: "bg-green-100 text-green-800", unpaid: "bg-amber-100 text-amber-800",
};
const Badge = ({ s }) => <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${STATUS_COLORS[s] || "bg-gray-100 text-gray-700"}`}>{s?.replace("_", " ")}</span>;

export default function ClientPortal() {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [tab, setTab] = useState("upcoming");
  const [profile, setProfile] = useState({ name: user?.name || "", phone: user?.phone || "" });
  const [addresses, setAddresses] = useState(user?.addresses || []);
  const [newAddr, setNewAddr] = useState("");
  const [bday, setBday] = useState({ month: user?.birthday_month || "", day: user?.birthday_day || "" });
  const [subscribed, setSubscribed] = useState(user?.marketing_subscribed !== false);

  const load = () => {
    api.get("/my/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/my/estimates").then((r) => setEstimates(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const now = new Date();
  const upcoming = bookings.filter((b) => new Date(b.start_dt) >= now && b.status !== "cancelled");
  const past = bookings.filter((b) => new Date(b.start_dt) < now || b.status === "cancelled");

  const request = async (b, action) => {
    try {
      await api.post(`/my/bookings/${b.booking_id}/request`, { action, message: "" });
      toast.success(`${action === "cancel" ? "Cancellation" : "Reschedule"} request sent to the owner.`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const pay = async (b, kind) => {
    try {
      const { data } = await api.post("/payments/checkout", { booking_id: b.booking_id, kind, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e)); }
  };

  const saveProfile = async () => {
    try {
      const payload = { ...profile, addresses, marketing_subscribed: subscribed };
      if (bday.month && bday.day) { payload.birthday_month = Number(bday.month); payload.birthday_day = Number(bday.day); }
      const { data } = await api.put("/auth/profile", payload);
      setUser(data); toast.success("Profile updated");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  const BookingCard = ({ b }) => (
    <div className="bg-white rounded-2xl border border-[#E6DEC3] p-5 shadow-[0_4px_20px_rgba(44,38,33,0.04)]" data-testid={`booking-card-${b.booking_number}`}>
      <div className="flex justify-between items-start gap-2">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] text-[#B87333] font-semibold">{b.booking_number}</p>
          <p className="font-serif text-xl text-[#2C2621] mt-1">{b.service_name}</p>
          <p className="text-sm text-[#635B52]">{b.tier_label}</p>
        </div>
        <Badge s={b.status} />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2 text-sm text-[#635B52]">
        <p className="flex items-center gap-1.5"><CalendarDays size={14} className="text-[#B87333]" /> {b.date}</p>
        <p className="flex items-center gap-1.5"><Clock size={14} className="text-[#B87333]" /> {b.start_time} CT</p>
        <p className="flex items-center gap-1.5"><DollarSign size={14} className="text-[#B87333]" /> ${(b.total_price || 0).toFixed(2)}</p>
        <p className="flex items-center gap-1.5"><Clock size={14} className="text-[#B87333]" /> {b.duration_minutes} min</p>
      </div>
      <p className="mt-3 text-sm text-[#635B52] flex items-start gap-1.5"><Home size={14} className="text-[#B87333] mt-0.5 shrink-0" /> {b.address}</p>
      {(b.discount_amount || 0) > 0 && <p className="mt-1 text-xs text-green-700 pl-5" data-testid={`discount-note-${b.booking_number}`}>Discount {b.discount_code} applied: −${(b.discount_amount || 0).toFixed(2)}</p>}
      {(b.tax_amount || 0) > 0 && <p className="mt-1 text-xs text-[#635B52] pl-5" data-testid={`tax-note-${b.booking_number}`}>Total ${(b.total_price || 0).toFixed(2)} incl. {b.tax_label || "Texas Sales Tax"} ${(b.tax_amount || 0).toFixed(2)}</p>}
      {b.client_request && <p className="mt-3 text-xs text-[#B87333]">Requested: {b.client_request.action} (awaiting owner)</p>}
      <div className="mt-3 flex flex-wrap gap-2 text-xs">
        <span className={`px-2 py-1 rounded-full ${b.deposit_paid ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>Deposit {b.deposit_paid ? "paid" : `due $${(b.deposit_amount || 0).toFixed(2)}`}</span>
        <span className={`px-2 py-1 rounded-full ${b.balance_paid ? "bg-green-100 text-green-800" : "bg-[#E6DEC3] text-[#635B52]"}`}>Balance {b.balance_paid ? "paid" : `$${(b.balance_amount || 0).toFixed(2)}`}</span>
        {b.recurring && b.recurring !== "none" && <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-800">{b.recurring}</span>}
      </div>
      {!b.deposit_paid && (b.deposit_amount || 0) > 0 && b.status !== "cancelled" && (
        <>
          <button onClick={() => pay(b, "deposit")} className="mt-3 w-full bg-[#B87333] hover:bg-[#A06127] text-white rounded-full py-2 text-sm font-medium transition-all" data-testid={`pay-deposit-${b.booking_number}`}>Pay 50% deposit — ${(b.deposit_amount || 0).toFixed(2)}</button>
          <StripeSecurityNote className="mt-3" />
        </>
      )}
      {b.deposit_paid && !b.balance_paid && (b.balance_amount || 0) > 0 && b.status !== "cancelled" && (
        <>
          <button onClick={() => pay(b, "balance")} className="mt-3 w-full bg-[#2C2621] hover:bg-[#1E1A17] text-white rounded-full py-2 text-sm font-medium transition-all" data-testid={`pay-balance-${b.booking_number}`}>Pay remaining balance — ${(b.balance_amount || 0).toFixed(2)}</button>
          <StripeSecurityNote className="mt-3" />
        </>
      )}
      {tab === "upcoming" && ["confirmed", "pending_approval", "rescheduled"].includes(b.status) && (
        <div className="mt-4 flex gap-2">
          <button onClick={() => request(b, "reschedule")} className="flex-1 border border-[#B87333]/40 hover:bg-[#F5F0E8] text-[#2C2621] rounded-full py-2 text-sm font-medium transition-all" data-testid={`req-reschedule-${b.booking_number}`}>Request reschedule</button>
          <button onClick={() => request(b, "cancel")} className="flex-1 border border-red-200 text-red-600 hover:bg-red-50 rounded-full py-2 text-sm font-medium transition-all" data-testid={`req-cancel-${b.booking_number}`}>Request cancel</button>
        </div>
      )}
      {b.review_token && !b.review_submitted && (
        <a href={`/review/${b.review_token}`} className="mt-3 block w-full text-center border border-[#B87333] text-[#B87333] hover:bg-[#B87333] hover:text-white rounded-full py-2 text-sm font-medium transition-all" data-testid={`write-review-${b.booking_number}`}>Write a Review</a>
      )}
      {b.review_submitted && <p className="mt-3 text-xs text-center text-[#635B52]" data-testid={`review-done-${b.booking_number}`}>✓ Thanks for your review!</p>}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FBF9F5]">
      <header className="bg-white border-b border-[#E6DEC3] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/"><img src="/masaya-logo.png" alt="Masaya" className="h-11 w-auto" /></Link>
          <div className="flex items-center gap-3">
            <Link to="/book" className="bg-[#B87333] text-white rounded-full px-4 py-2 text-sm font-medium">Book again</Link>
            <button onClick={doLogout} className="text-[#635B52] hover:text-[#2C2621] flex items-center gap-1 text-sm" data-testid="portal-logout"><LogOut size={16} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <h1 className="font-serif text-3xl sm:text-4xl text-[#2C2621]">Welcome, {user?.name?.split(" ")[0]}</h1>
        <p className="mt-1 text-[#635B52]">Manage your appointments and account.</p>

        <div className="mt-6 flex gap-2 flex-wrap" data-testid="portal-tabs">
          {[["upcoming", "Upcoming", CalendarDays], ["past", "History", Clock], ["estimates", "Estimates", FileText], ["profile", "Profile", User]].map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)} className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-all ${tab === k ? "bg-[#2C2621] text-white" : "bg-white border border-[#E6DEC3] text-[#635B52] hover:border-[#B87333]/40"}`} data-testid={`tab-${k}`}>
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "upcoming" && (upcoming.length ? <div className="grid sm:grid-cols-2 gap-4">{upcoming.map((b) => <BookingCard key={b.booking_id} b={b} />)}</div> : <Empty text="No upcoming appointments." />)}
          {tab === "past" && (past.length ? <div className="grid sm:grid-cols-2 gap-4">{past.map((b) => <BookingCard key={b.booking_id} b={b} />)}</div> : <Empty text="No past appointments yet." />)}
          {tab === "estimates" && (estimates.length ? (
            <div className="grid sm:grid-cols-2 gap-4">
              {estimates.map((e) => (
                <div key={e.estimate_id} className="bg-white rounded-2xl border border-[#E6DEC3] p-5" data-testid={`estimate-card-${e.estimate_number}`}>
                  <div className="flex justify-between items-start">
                    <p className="text-xs uppercase tracking-[0.15em] text-[#B87333] font-semibold">{e.estimate_number}</p>
                    <Badge s={e.status} />
                  </div>
                  <p className="mt-2 text-[#2C2621] font-medium">{e.requested_service || "Deep Cleaning"}</p>
                  <p className="text-sm text-[#635B52]">{e.address}</p>
                  {e.final_price != null && <p className="mt-2 text-[#B87333] font-semibold">Quote: ${Number(e.final_price).toFixed(2)}</p>}
                  {e.owner_note && <p className="mt-1 text-sm text-[#635B52]">Note: {e.owner_note}</p>}
                  {["quote_sent", "pending_deposit"].includes(e.status) && (
                    <a href={`/quote/${e.estimate_number}`} className="mt-3 inline-block bg-[#B87333] text-white rounded-full px-4 py-2 text-sm font-medium" data-testid={`view-quote-${e.estimate_number}`}>View &amp; accept quote</a>
                  )}
                </div>
              ))}
            </div>
          ) : <Empty text="No estimate requests yet." />)}
          {tab === "profile" && (
            <div className="max-w-md bg-white rounded-2xl border border-[#E6DEC3] p-6 space-y-4" data-testid="profile-form">
              <div><label className="text-sm font-medium text-[#2C2621]">Name</label>
                <input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className="mt-1 w-full rounded-xl border border-[#E6DEC3] px-4 py-3" data-testid="profile-name" /></div>
              <div><label className="text-sm font-medium text-[#2C2621]">Phone</label>
                <input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className="mt-1 w-full rounded-xl border border-[#E6DEC3] px-4 py-3" data-testid="profile-phone" /></div>
              <div><label className="text-sm font-medium text-[#635B52]">Email</label>
                <input value={user?.email} disabled className="mt-1 w-full rounded-xl border border-[#E6DEC3] px-4 py-3 bg-[#F5F0E8] text-[#635B52]" /></div>
              <div data-testid="birthday-field">
                <label className="text-sm font-medium text-[#2C2621]">Birthday (for a $25 gift!)</label>
                <p className="text-xs text-[#635B52] mb-1">Month & day only — no year needed.</p>
                <div className="flex gap-2">
                  <select value={bday.month} onChange={(e) => setBday({ ...bday, month: e.target.value })} className="flex-1 rounded-xl border border-[#E6DEC3] px-3 py-2.5" data-testid="birthday-month">
                    <option value="">Month</option>
                    {["January","February","March","April","May","June","July","August","September","October","November","December"].map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                  </select>
                  <input type="number" min="1" max="31" value={bday.day} onChange={(e) => setBday({ ...bday, day: e.target.value })} placeholder="Day" className="w-24 rounded-xl border border-[#E6DEC3] px-3 py-2.5" data-testid="birthday-day" />
                </div>
                {user?.birthday_code && <p className="mt-2 text-sm text-[#B87333]">🎁 Your birthday code: <strong>{user.birthday_code}</strong> ($25 off, min $125)</p>}
              </div>
              <label className="flex items-center gap-2 text-sm text-[#2C2621] cursor-pointer" data-testid="marketing-optin">
                <input type="checkbox" checked={subscribed} onChange={(e) => setSubscribed(e.target.checked)} className="w-4 h-4 accent-[#B87333]" data-testid="marketing-checkbox" />
                Email me promotions, seasonal offers, and my birthday gift
              </label>
              <div data-testid="address-book">
                <label className="text-sm font-medium text-[#2C2621]">Saved service addresses</label>
                <div className="mt-2 space-y-2">
                  {addresses.map((a, i) => (
                    <div key={i} className="flex items-center gap-2 bg-[#F5F0E8] rounded-xl px-3 py-2" data-testid={`address-item-${i}`}>
                      <Home size={15} className="text-[#B87333] shrink-0" />
                      <span className="flex-1 text-sm text-[#2C2621]">{a}</span>
                      <button onClick={() => setAddresses(addresses.filter((_, j) => j !== i))} className="text-red-500 hover:text-red-700 text-xs" data-testid={`address-remove-${i}`}>Remove</button>
                    </div>
                  ))}
                  {!addresses.length && <p className="text-sm text-[#635B52]">No saved addresses yet.</p>}
                </div>
                <div className="mt-2 flex gap-2">
                  <input value={newAddr} onChange={(e) => setNewAddr(e.target.value)} placeholder="Add a service address" className="flex-1 rounded-xl border border-[#E6DEC3] px-4 py-2.5 text-sm" data-testid="address-input" />
                  <button onClick={() => { const v = newAddr.trim(); if (v && !addresses.includes(v)) { setAddresses([...addresses, v]); setNewAddr(""); } }} className="bg-[#2C2621] text-white rounded-full px-4 text-sm" data-testid="address-add">Add</button>
                </div>
              </div>
              <button onClick={saveProfile} className="bg-[#B87333] text-white rounded-full px-6 py-3 font-medium" data-testid="profile-save">Save changes</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Empty = ({ text }) => (
  <div className="bg-white rounded-2xl border border-[#E6DEC3] p-12 text-center text-[#635B52]" data-testid="empty-state">
    <Home className="mx-auto text-[#B87333]/50" size={40} />
    <p className="mt-3">{text}</p>
    <Link to="/book" className="mt-4 inline-block bg-[#B87333] text-white rounded-full px-6 py-2.5 font-medium">Book a cleaning</Link>
  </div>
);
